import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, RefreshCw, Send, PlayCircle } from "lucide-react";

export default function Integrations() {
  return (
    <div className="space-y-4" data-testid="integrations-root">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Integrations</h1>
        <p className="text-sm text-slate-600 mt-1">Google Sheets sync · Resend email digest · Document AI OCR · Nightly detectors · Legacy importer</p>
      </div>
      <Card className="p-3 border-2 border-blue-300 bg-blue-50 rounded-sm" data-testid="setup-guide-card">
        <div className="text-sm text-blue-900">
          <strong className="uppercase tracking-wider text-xs">Setup guide</strong>
          <p className="mt-1">Step-by-step instructions for getting Google Sheets, Resend, and Document AI keys are in <code className="bg-white px-1 border border-blue-200 rounded">/app/memory/INTEGRATION_SETUP_GUIDE.md</code>. The short version:</p>
          <ol className="list-decimal ml-5 mt-1 space-y-0.5 text-xs">
            <li><strong>Sheets:</strong> Google Cloud → new service-account JSON → share sheet with its email → paste JSON below.</li>
            <li><strong>Email:</strong> resend.com → API Key → paste + set From address + recipients.</li>
            <li><strong>OCR:</strong> enable Document AI in the same GCP project → create a processor → paste processor ID.</li>
          </ol>
          <p className="mt-1 text-xs">All three are optional — the app is fully usable without any of them enabled.</p>
        </div>
      </Card>
      <Tabs defaultValue="sheets">
        <TabsList>
          <TabsTrigger value="sheets" data-testid="tab-sheets">Google Sheets</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">Email Digest</TabsTrigger>
          <TabsTrigger value="ocr" data-testid="tab-ocr">OCR (Document AI)</TabsTrigger>
          <TabsTrigger value="detectors" data-testid="tab-detectors">Nightly Detectors</TabsTrigger>
          <TabsTrigger value="importer" data-testid="tab-importer">Legacy Importer</TabsTrigger>
        </TabsList>
        <TabsContent value="sheets"><SheetsTab /></TabsContent>
        <TabsContent value="email"><EmailTab /></TabsContent>
        <TabsContent value="ocr"><OcrTab /></TabsContent>
        <TabsContent value="detectors"><DetectorsTab /></TabsContent>
        <TabsContent value="importer"><ImporterTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function SheetsTab() {
  const [cfg, setCfg] = useState(null);
  const [saJson, setSaJson] = useState("");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, l] = await Promise.all([
      api.get("/integrations/sheets/config"),
      api.get("/integrations/sheets/log").catch(() => ({ data: [] })),
    ]);
    setCfg(c.data);
    setLog(l.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...cfg, service_account_json: saJson || null };
      const r = await api.post("/integrations/sheets/config", payload);
      toast.success("Saved. Share your sheet with: " + (r.data.share_with || cfg.service_account_email));
      setSaJson("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/sheets/run");
      toast.success("Sync run: " + JSON.stringify(r.data).slice(0, 200));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const backfillNow = async () => {
    if (!window.confirm(
      "Push EVERY table currently in the database to your Google Sheet " +
      "(shifts, debtors, payments, readings, adjustments, lubricants, POs, flags, " +
      "manager daily, attendance, debtor payments)?\n\n" +
      "Existing mirror tabs will be overwritten. The main Shifts and Debtors tabs " +
      "use the normal bidirectional rules."
    )) return;
    setBusy(true);
    try {
      const r = await api.post("/integrations/sheets/backfill");
      const shifts = r.data?.shifts_and_debtors || {};
      const mirror = r.data?.mirror?.tabs || {};
      const tabCounts = Object.entries(mirror)
        .map(([tab, v]) => `${tab}: ${v.rows ?? "err"}`)
        .join(" · ");
      toast.success(`Backfill done · Shifts sync: ${JSON.stringify(shifts).slice(0, 60)}\n${tabCounts}`, { duration: 8000 });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <div className="mt-3 space-y-3">
      <Card className="p-4 border-2 border-slate-300 rounded-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Enabled</Label>
            <div className="flex gap-2 mt-1">
              {[true, false].map((v) => (
                <button key={String(v)} type="button" onClick={() => setCfg({ ...cfg, enabled: v })}
                        data-testid={`sheets-enabled-${v}`}
                        className={"px-3 h-10 border-2 font-bold text-sm rounded-sm " + (cfg.enabled === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300")}>
                  {v ? "ON" : "OFF"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Spreadsheet ID</Label>
            <Input data-testid="sheets-id" value={cfg.spreadsheet_id} onChange={(e) => setCfg({ ...cfg, spreadsheet_id: e.target.value })} placeholder="1BxiMVs0XRY..." className="h-10 mt-1 border border-slate-300 font-mono text-sm" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Shifts tab</Label>
            <Input value={cfg.shifts_tab} onChange={(e) => setCfg({ ...cfg, shifts_tab: e.target.value })} className="h-10 mt-1 border border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Debtors tab</Label>
            <Input value={cfg.debtors_tab} onChange={(e) => setCfg({ ...cfg, debtors_tab: e.target.value })} className="h-10 mt-1 border border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Direction</Label>
            <div className="flex gap-2 mt-1">
              {["both","push","pull"].map((v) => (
                <button key={v} type="button" onClick={() => setCfg({ ...cfg, direction: v })}
                        className={"px-3 h-10 border-2 font-bold text-sm rounded-sm " + (cfg.direction === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300")}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Interval (min)</Label>
            <Input type="number" min={1} value={cfg.interval_min} onChange={(e) => setCfg({ ...cfg, interval_min: Number(e.target.value) })} className="h-10 mt-1 border border-slate-300 num" />
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Service Account JSON</Label>
          <p className="text-xs text-slate-500 mt-1">
            Paste the whole JSON key from GCP → IAM → Service Accounts → Keys → Add Key → JSON.
            Then <b>share your Google Sheet</b> with the SA email below.
          </p>
          {cfg.has_service_account && (
            <div className="text-xs mt-1 bg-emerald-50 border border-emerald-300 p-2 rounded-sm">
              Already configured. SA email: <b className="font-mono">{cfg.service_account_email || "(hidden)"}</b>
              <button className="ml-2 underline" onClick={() => navigator.clipboard.writeText(cfg.service_account_email)}><Copy size={12} className="inline" /> copy</button>
            </div>
          )}
          <Textarea data-testid="sheets-sa-json" value={saJson} onChange={(e) => setSaJson(e.target.value)}
                    placeholder='{"type":"service_account","project_id":"...", ...}'
                    rows={6}
                    className="mt-1 border border-slate-300 font-mono text-xs" />
        </div>

        <div className="flex gap-2 mt-4 flex-wrap">
          <Button data-testid="save-sheets-btn" onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
          <Button data-testid="run-sheets-btn" variant="outline" onClick={runNow} disabled={busy} className="border-slate-300"><RefreshCw size={14} className="mr-1" /> Run sync now</Button>
          <Button data-testid="backfill-sheets-btn" onClick={backfillNow} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white">
            <PlayCircle size={14} className="mr-1" /> Backfill everything now
          </Button>
        </div>

        <div className="mt-3 p-3 border border-emerald-300 bg-emerald-50 rounded-sm text-xs text-emerald-900">
          <div className="font-bold mb-1">Disaster-recovery mirror</div>
          The <em>backfill</em> button pushes every operational table currently in the app database
          to your Google Sheet — as separate tabs:
          <span className="font-mono mx-1">Payments · Readings · Adjustments · Lubricants · Fuel_POs · Flags · Manager_Daily · Attendance · Debtor_Payments</span>.
          After that, the scheduler keeps the mirror fresh every {cfg.interval_min || 5} minutes so
          your Google Sheet is always a complete, up-to-the-minute backup of the app.
        </div>
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2">Recent Sync Runs</h3>
        {log.length === 0 && <div className="text-sm text-slate-500">No sync runs yet.</div>}
        <ul className="text-xs space-y-1 font-mono">
          {log.slice(0, 10).map((l) => (
            <li key={l.id || l.at} className="border-b border-slate-100 py-1">{l.at?.slice(0,19)} · {JSON.stringify(l).slice(0, 300)}</li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function EmailTab() {
  const [cfg, setCfg] = useState(null);
  const [key, setKey] = useState("");
  const [toStr, setToStr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/integrations/email/config");
    setCfg(r.data);
    setToStr((r.data.to_addresses || []).join(", "));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...cfg, to_addresses: toStr.split(/[,\s]+/).filter(Boolean), resend_api_key: key || null };
      await api.post("/integrations/email/config", payload);
      toast.success("Saved");
      setKey("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/email/send_now");
      if (r.data.sent) toast.success("Sent to " + (r.data.to || []).join(", "));
      else toast.warning("Skipped: " + (r.data.reason || r.data.error || "unknown"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <Card className="p-4 border-2 border-slate-300 rounded-sm mt-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Enabled</Label>
          <div className="flex gap-2 mt-1">
            {[true, false].map((v) => (
              <button key={String(v)} type="button" onClick={() => setCfg({ ...cfg, enabled: v })}
                      className={"px-3 h-10 border-2 font-bold text-sm rounded-sm " + (cfg.enabled === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300")}>
                {v ? "ON" : "OFF"}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Resend API Key {cfg.has_api_key && <span className="text-emerald-700">(saved)</span>}</Label>
          <Input data-testid="resend-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="re_xxxxxxxx" className="h-10 mt-1 border border-slate-300 font-mono" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">From address</Label>
          <Input value={cfg.from_address} onChange={(e) => setCfg({ ...cfg, from_address: e.target.value })} className="h-10 mt-1 border border-slate-300" />
          <p className="text-xs text-slate-500 mt-1">Must be on a verified Resend domain, or <code>onboarding@resend.dev</code> for testing.</p>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">To addresses (comma or space separated)</Label>
          <Input data-testid="to-addresses" value={toStr} onChange={(e) => setToStr(e.target.value)} placeholder="abhay@..., snehdeep@..." className="h-10 mt-1 border border-slate-300" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Send hour (IST)</Label>
          <Input type="number" min={0} max={23} value={cfg.hour_ist} onChange={(e) => setCfg({ ...cfg, hour_ist: Number(e.target.value) })} className="h-10 mt-1 border border-slate-300 num" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Send minute (IST)</Label>
          <Input type="number" min={0} max={59} value={cfg.minute_ist} onChange={(e) => setCfg({ ...cfg, minute_ist: Number(e.target.value) })} className="h-10 mt-1 border border-slate-300 num" />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button data-testid="save-email-btn" onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
        <Button data-testid="send-email-btn" variant="outline" onClick={sendNow} disabled={busy} className="border-slate-300"><Send size={14} className="mr-1" /> Send digest now</Button>
      </div>
    </Card>
  );
}

function OcrTab() {
  const [cfg, setCfg] = useState({ enabled: false, gcp_project_id: "", gcp_location: "us", processor_id_default: "", processor_map: {} });
  const [mapStr, setMapStr] = useState("{}");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get("/integrations/ocr/config");
      const c = r.data || {};
      setCfg({ enabled: c.enabled || false, gcp_project_id: c.gcp_project_id || "", gcp_location: c.gcp_location || "us", processor_id_default: c.processor_id_default || "", processor_map: c.processor_map || {} });
      setMapStr(JSON.stringify(c.processor_map || {}, null, 2));
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      let pm = {};
      try { pm = JSON.parse(mapStr); } catch { toast.error("processor_map is not valid JSON"); return; }
      await api.post("/integrations/ocr/config", { ...cfg, processor_map: pm });
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-4 border-2 border-slate-300 rounded-sm mt-3">
      <p className="text-xs text-slate-600 mb-3">Uses the same service account JSON you pasted for Sheets. Grant it <b>Document AI User</b> on the GCP project.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Enabled</Label>
          <div className="flex gap-2 mt-1">
            {[true, false].map((v) => (
              <button key={String(v)} type="button" onClick={() => setCfg({ ...cfg, enabled: v })}
                      className={"px-3 h-10 border-2 font-bold text-sm rounded-sm " + (cfg.enabled === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300")}>
                {v ? "ON" : "OFF"}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">GCP Project ID</Label>
          <Input value={cfg.gcp_project_id} onChange={(e) => setCfg({ ...cfg, gcp_project_id: e.target.value })} className="h-10 mt-1 border border-slate-300 font-mono" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Location</Label>
          <select value={cfg.gcp_location} onChange={(e) => setCfg({ ...cfg, gcp_location: e.target.value })} className="w-full h-10 mt-1 border border-slate-300 rounded-sm px-2 bg-white">
            <option value="us">us</option>
            <option value="eu">eu</option>
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Default Processor ID</Label>
          <Input value={cfg.processor_id_default} onChange={(e) => setCfg({ ...cfg, processor_id_default: e.target.value })} className="h-10 mt-1 border border-slate-300 font-mono" />
        </div>
      </div>
      <div className="mt-3">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Processor map (JSON — slot_type → processor_id)</Label>
        <Textarea value={mapStr} onChange={(e) => setMapStr(e.target.value)} rows={5} className="mt-1 border border-slate-300 font-mono text-xs" placeholder='{"PAYMENT":"...","DEBTOR":"..."}' />
      </div>
      <Button data-testid="save-ocr-btn" onClick={save} disabled={busy} className="mt-4 bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
    </Card>
  );
}

function DetectorsTab() {
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState("");

  const load = async () => {
    const r = await api.get("/integrations/detectors/log");
    setLog(r.data);
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/detectors/run", null, { params: date ? { target_date: date } : {} });
      toast.success(`Detectors ran: ${JSON.stringify(r.data)}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 space-y-3">
      <Card className="p-4 border-2 border-slate-300 rounded-sm">
        <p className="text-sm text-slate-600">
          Runs nightly at <b>02:00 IST</b>. Checks for: MISSING_ENTRY, NO_PROOF (past grace), SHORTAGE_TIER (0.2/0.3/0.4%), SALES_DROP (&gt;7% below 30d avg), STOCK_NEGATIVE, DEPOSIT_LATE (CMS slip &gt; SLA past shift end).
        </p>
        <div className="flex gap-2 items-end mt-3 flex-wrap">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Target date (blank = yesterday)</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 mt-1 border border-slate-300" />
          </div>
          <Button data-testid="run-detectors-btn" onClick={runNow} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white"><PlayCircle size={14} className="mr-1" /> Run now</Button>
        </div>
      </Card>
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2">Recent nightly-detector flags</h3>
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Type</th><th>Date</th><th>Msg</th><th>₹</th></tr></thead>
          <tbody>
            {log.map((f) => (
              <tr key={f.flag_id}>
                <td className="font-mono text-xs">{f.type}</td>
                <td>{f.date}</td>
                <td className="text-xs">{f.message}</td>
                <td className="num font-bold text-red-600">{f.rupee_impact}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function ImporterTab() {
  const [file, setFile] = useState(null);
  const [dryStats, setDryStats] = useState(null);
  const [busy, setBusy] = useState(false);

  async function upload(commit) {
    if (!file) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(commit ? "/importer/xlsx/commit" : "/importer/xlsx/dry_run", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDryStats(r.data);
      toast.success(commit ? "Committed" : "Dry-run complete");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="mt-3 space-y-3">
      <Card className="p-4 border-2 border-slate-300 rounded-sm">
        <p className="text-sm text-slate-600">
          Upload the legacy <b>AppSheet .xlsx</b> (must include tabs: M_Nozzles, M_Payment_Modes, M_Attendee_Name, M_Cashier_Name, Admin_Access, Pricing_Data, T_Shifts, T_Nozzle_Readings, T_Shift_Payments, Nozzle_Adjustments, Edit Control).
          Run <b>Dry Run</b> first to see the counts, then <b>Commit</b>.
        </p>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <input data-testid="importer-file" type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0])} className="text-sm" />
          <Button data-testid="importer-dryrun-btn" variant="outline" onClick={() => upload(false)} disabled={busy || !file} className="border-slate-300">Dry Run</Button>
          <Button data-testid="importer-commit-btn" onClick={() => upload(true)} disabled={busy || !file} className="bg-slate-900 hover:bg-slate-800 text-white">Commit Import</Button>
        </div>
      </Card>

      {dryStats && (
        <Card className="p-3 border-2 border-slate-300 rounded-sm">
          <h3 className="font-bold text-slate-800 mb-2">{dryStats.dry_run ? "Dry Run Results" : "Committed"}</h3>
          <div className="text-xs mb-2">Tabs: {(dryStats.tabs_found || []).join(", ")}</div>
          <table className="dense-table w-full text-sm">
            <thead><tr><th>Section</th><th>Rows</th><th>Created</th><th>Skipped</th></tr></thead>
            <tbody>
              {Object.entries(dryStats.stats || {}).map(([k, v]) => (
                <tr key={k}>
                  <td className="font-bold">{k}</td>
                  <td className="num">{v.count}</td>
                  <td className="num text-emerald-700">{v.created}</td>
                  <td className="num text-slate-500">{v.skipped}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
